<template>
  <base-stack class="photos" v-if="tour">
    <app-cell
      v-if="form || tour.photos"
      :edit="!!form"
      :loading="!!form && form.isLoading"
      :deletable="false"
      @edit="onEdit"
      @save="onSave"
      @cancel="onCancel"
    >
      <base-stack v-if="tour.photos?.photoPrint" slot="content" :spacing="4">
        <h2 class="title">{{ tour.photos.photoPrint.title }}</h2>
        <div v-html="tour.photos.photoPrint.description" />
        <base-stack class="box" as="fieldset" :spacing="2">
          <legend>Photo print orders</legend>
          <p class="entry">
            <span class="entry__label">Stop Date</span>
            <span class="entry__value">
              {{ tour.photos.stopDate | dayjs('MMM DD, YYYY (HH:mm)') }}
            </span>
          </p>
          <p class="entry">
            <span class="entry__label">Expected Delivery</span>
            <span class="entry__value">
              {{ tour.photos.expectedDeliveryAt | dayjs('MMM DD, YYYY') }}
            </span>
          </p>
        </base-stack>

        <div class="variants" v-if="showPhotoVariants?.length">
          <h3>Show photo variants</h3>
          <div class="variant" v-for="variant in showPhotoVariants" :key="variant.id">
            <div class="variant__info">
              <base-stack direction="row" :spacing="2">
                <strong>{{ variant.name }}</strong>
              </base-stack>
              <strong :class="{ 'has-price': variant.hasPrice }">{{ variant.price | usd.credits }}</strong>
            </div>
          </div>
        </div>

        <div class="variants" v-if="meetAndGreetPhotoVariants?.length">
          <h3>Secret Gathering photo variants</h3>
          <div class="variant" v-for="variant in meetAndGreetPhotoVariants" :key="variant.id">
            <div class="variant__info">
              <base-stack direction="row" :spacing="2">
                <strong>{{ variant.name }}</strong>
              </base-stack>
              <strong :class="{ 'has-price': variant.hasPrice }">{{ variant.price | usd.credits }}</strong>
            </div>
          </div>
        </div>
      </base-stack>

      <base-stack slot="form" as="form" v-if="form">
        <base-form-input label="Title">
          <input v-model="form.title" placeholder="Title" />
        </base-form-input>
        <base-form-input label="Description">
          <app-html-editor v-model="form.description" />
        </base-form-input>
        <base-form-input label="Base Price">
          <input v-model.number="form.price" placeholder="Price" type="number" />
          <template slot="hint">{{
            inputPriceInUsd ? `= ${inputPriceInUsd}` : 'In USD x 1000, i.e. $100.00 = 100000'
          }}</template>
        </base-form-input>
        <base-form-input label="Photo Ordering Stop Date">
          <base-date-input v-model="form.stopDate" type="datetime-local" placeholder="Stop Date" />
          <template slot="hint"> When photo ordering should stop being available for this tour </template>
        </base-form-input>
        <base-form-input label="Expected Delivery Date">
          <base-date-input v-model="form.expectedDeliveryAt" placeholder="Expected Delivery" />
          <template slot="hint"> When customers can expect to receive their photo orders </template>
        </base-form-input>

        <base-stack class="form__variants">
          <app-cell
            class="variant-form"
            v-for="variantForm in form.variantInputs"
            :key="variantForm.key"
            :editable="false"
          >
            <base-stack>
              <base-form-input class="form__input" label="Name">
                <input v-model="variantForm.name" placeholder="e.g. 4x6 inch, 8x10 inch, etc." />
              </base-form-input>
              <base-form-input v-if="form.variantInputs.length > 1" class="form__input" label="Sort order">
                <input v-model.number="variantForm.sortOrder" placeholder="Sort order" type="number" />
              </base-form-input>
              <base-form-input class="form__input" label="Price">
                <input v-model.number="variantForm.price" placeholder="Same as base price" type="number" />
                <template slot="hint">{{
                  variantForm.price ? `= $${variantForm.price / 1000}` : 'In USD x 1000, i.e. $100.00 = 100000'
                }}</template>
              </base-form-input>
              <base-form-input v-if="'shopifyIdEU' in variantForm" label="EU Shopify" class="form__input">
                <input v-model="variantForm.shopifyIdEU" placeholder="Shopify id (EU)" />
              </base-form-input>
              <base-form-input v-if="'shopifyIdUS' in variantForm" label="US Shopify" class="form__input">
                <input v-model="variantForm.shopifyIdUS" placeholder="Shopify id (US)" />
              </base-form-input>
              <base-stack class="box" as="fieldset">
                <legend>Dimensions</legend>
                <p>In inches, not visible for users</p>
                <base-form-input class="form__input" label="Print width">
                  <input v-model.number="variantForm.printWidth" placeholder="Width (inches)" type="number" />
                </base-form-input>
                <base-form-input class="form__input" label="Print height">
                  <input v-model.number="variantForm.printHeight" placeholder="Height (inches)" type="number" />
                </base-form-input>
              </base-stack>
              <base-stack class="box" as="fieldset">
                <legend>Applicability</legend>
                <base-checkbox v-model="variantForm.applyToShowPhotos">Regular photos</base-checkbox>
                <base-checkbox v-model="variantForm.applyToMeetAndGreetPhotos">Secret Gathering photos</base-checkbox>
              </base-stack>
            </base-stack>
            <template slot="actions">
              <base-button v-if="form.variantInputs.length > 1" @click="onDeleteVariant(variantForm)" :danger="true"
                >Delete</base-button
              >
            </template>
          </app-cell>
          <div class="variants__add">
            <base-button @click="onAddVariant" type="button">Add variant</base-button>
          </div>
        </base-stack>
      </base-stack>
    </app-cell>

    <base-stack v-else>
      <base-stack direction="row" :spacing="2">
        <base-button @click="onEdit">Add Photo Print</base-button>
      </base-stack>
    </base-stack>
  </base-stack>
</template>

<script lang="ts">
import AppCell from '@/components/Cell.vue';
import AppHtmlEditor from '@/components/HtmlEditor.vue';
import { PhotoPrintVariantForm } from '@/forms/purchasable/variant/PhotoPrintVariantForm';
import { PhotoPrintVariant, Tour } from '@/types/api';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { VariantFragments } from '../apollo/fragments/variant';
import { TourPhotosForm } from '../forms/tour/TourPhotosForm';

@Component({
  components: {
    AppCell,
    AppHtmlEditor,
  },
  apollo: {
    tour: {
      query: gql`
        query Tour($id: ID!) {
          tour(id: $id) {
            id
            photos {
              stopDate
              expectedDeliveryAt
              photoPrint {
                id
                title
                description
                price
                ... on PhotoPrint {
                  variants {
                    ...PhotoPrintVariant
                  }
                }
              }
            }
          }
        }

        ${VariantFragments.PhotoPrintVariant}
      `,
      variables() {
        return {
          id: this.tourId,
        };
      },
      fetchPolicy: 'cache-and-network',
    },
  },
})
export default class TourPhotos extends Vue {
  @Prop({ required: true })
  public tourId!: string;

  tour?: Tour;

  public form = null as TourPhotosForm | null;

  public get inputPriceInUsd(): string | null {
    const price = this.form?.price;
    return price ? `$${price / 1000}` : null;
  }

  public get showPhotoVariants(): PhotoPrintVariant[] {
    return this.tour?.photos?.photoPrint?.variants?.filter((variant) => variant.applyToShowPhotos) ?? [];
  }

  public get meetAndGreetPhotoVariants(): PhotoPrintVariant[] {
    return this.tour?.photos?.photoPrint?.variants?.filter((variant) => variant.applyToMeetAndGreetPhotos) ?? [];
  }

  public formatDate(date: string | Date): string {
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  }

  public priceInUsd(price: number): string {
    return `$${price / 1000}`;
  }

  onEdit() {
    if (!this.tour) return;
    this.form = new TourPhotosForm(this.tour);
  }

  async onSave() {
    await this.form?.save();
    this.form = null;
  }

  onCancel() {
    this.form = null;
  }

  onAddVariant() {
    if (this.form) {
      this.form.variantInputs.push(new PhotoPrintVariantForm());
    }
  }

  onDeleteVariant(variantForm: PhotoPrintVariantForm) {
    if (this.form) {
      this.form.variantInputs = this.form.variantInputs.filter((variant) => variantForm.key !== variant.key);
    }
  }
}
</script>

<style lang="scss" scoped>
.slider {
  max-width: 200px;
}

.title {
  margin-bottom: 0;
}

.photo-details {
  p {
    margin: 8px 0;

    strong {
      font-weight: bold;
      margin-right: 8px;
    }
  }
}

.price {
  b {
    font-weight: bold;
  }
  strong {
    margin-left: 8px;
  }
}

form {
  max-width: 600px;
}

.variants {
  margin-top: 16px;

  h3 {
    margin-bottom: 8px;
    font-weight: bold;
  }

  .variant {
    padding: 8px 12px;
    border: 1px solid $color-theme-red;
    margin-bottom: 4px;

    .variant__info {
      display: flex;
      justify-content: space-between;
      align-items: center;

      strong {
        color: $color-theme-yellow;

        &.has-price {
          color: $color-theme-red;
        }
      }
    }
  }
}

.box {
  border: 1px solid $color-theme-red;
  padding: 16px;

  &-title {
    margin-top: 0;
    color: $color-theme-red;
  }
}

.entry {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  column-gap: 8px;

  &__value {
    float: right;
    font-weight: bold;
    strong {
      margin-left: 2px;
      font-size: 1rem;
    }
    &.danger {
      color: $color-theme-red;
    }
    &.success {
      color: $color-success;
    }
  }
}

.form__variants {
  .variant-form {
    border: 1px solid $color-theme-red;
  }

  .variants__add {
    margin-top: 8px;
  }
}
</style>
